/**
 * Contrato común de autenticación.
 *
 * La app (auth-provider, api/client) habla SIEMPRE contra esta interfaz,
 * sin saber si por debajo está Keycloak (ROPC embebido) o Microsoft Entra
 * ID (MSAL). El selector de `index.ts` decide la implementación a partir de
 * `VITE_AUTH_PROVIDER`.
 */

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  roles: string[];
}

/** Error de autenticación con status HTTP (0 = error de red/cliente). */
export class AuthError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = "AuthError";
    this.status = status;
  }
}

/**
 * API que toda implementación de proveedor debe exponer. Es exactamente lo
 * que consumen `api/client.ts` y `auth-provider.tsx`, así que mantener estas
 * firmas estables garantiza que el resto de la app no cambie.
 */
export interface AuthProvider {
  /** Identificador del proveedor activo. */
  readonly kind: "keycloak" | "entra";

  /**
   * true si este proveedor usa un formulario embebido de usuario/contraseña
   * (keycloak). false si el login es vía redirección/popup externo (entra).
   */
  readonly usesCredentialsForm: boolean;

  /** Inicialización idempotente del proveedor (MSAL la necesita). */
  initialize(): Promise<void>;

  /**
   * Login con credenciales (keycloak). En entra no aplica: lanza para no
   * usarse por error; el login externo va por `loginInteractive()`.
   */
  login(username: string, password: string): Promise<AuthUser>;

  /** Login interactivo externo (entra: redirect/popup). En keycloak no aplica. */
  loginInteractive(): Promise<void>;

  /** Cierra sesión y limpia el estado local. */
  logout(): Promise<void>;

  /**
   * Renueva el access token si hace falta y devuelve uno vigente, o null si
   * la sesión ya no puede renovarse. La usan los helpers HTTP en cada request.
   */
  refreshIfNeeded(): Promise<string | null>;

  /** Access token vigente sin tocar la red, o null. */
  getAccessToken(): string | null;

  /** Usuario actual derivado del estado local, o null. */
  getCurrentUser(): AuthUser | null;

  /** Al cargar la app: devuelve el usuario si hay sesión recuperable. */
  restoreSession(): Promise<AuthUser | null>;
}
